import React, { useState, useEffect } from 'react';
import {
    Box, Grid, Stack, TableContainer, Table, TableHead, TableRow,
    TableCell, TableBody, Paper, FormControl, InputLabel, Select,
    MenuItem, Button, TextField, Chip, Typography
} from '@mui/material';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { useParams } from 'react-router-dom';
import { postAPI, getAPI, deleteAPI, putAPI } from '../../network';
import { tableData, tableHeader, warningMsg } from '../../utils';

const Distribution = () => {
    const { siteId } = useParams();
    const [editId, setEditId] = useState('');
    const [emailInput, setEmailInput] = useState('');
    const [emailList, setEmailList] = useState([]);
    const [distributionList, setDistributionList] = useState([]);
    const [reportTypes, setReportTypes] = useState([]);
    const [selectedReport, setSelectedReport] = useState('');

    useEffect(() => {
        getReportTypes();
        getDistributionList();
    }, []);

    const getReportTypes = async () => {
        const data = await getAPI(`/report-types?siteId=${siteId}`);
        if (data) {
            setReportTypes(data);
        }
    };

    const getDistributionList = async () => {
        const data = await getAPI(`/distribution?siteId=${siteId}`);
        if (data) {
            setDistributionList(data);
        }
    };

    const handleEmailChange = (e) => {
        setEmailInput(e.target.value);
    };

    const handleEmailSubmit = (e) => {
        e.preventDefault();
        if (!emailInput.trim()) return;

        const isValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailInput.trim());
        if (!isValid) {
            warningMsg('Invalid email format');
            return;
        }

        if (!emailList.includes(emailInput.trim())) {
            setEmailList([emailInput.trim(), ...emailList]);
        }
        setEmailInput('');
    };

    const handleDeleteEmail = (index) => {
        setEmailList(prev => prev.filter((_, i) => i !== index));
    };

    const resetForm = () => {
        setEditId('');
        setSelectedReport('');
        setEmailList([]);
        setEmailInput('');
    };

    const handleSubmit = async () => {
        if (!selectedReport) return warningMsg('Please select a report type');
        if (!emailList.length) return warningMsg('Please enter at least one email');

        const duplicate = distributionList.some(item => item.reportType === selectedReport && !editId);
        if (duplicate) return warningMsg(`${selectedReport} already exists. Please edit instead.`);

        const payload = {
            siteId,
            reportType: selectedReport,
            email: emailList
        };

        const result = editId
            ? await putAPI(`/distribution/${editId}`, payload)
            : await postAPI('/distribution', payload);

        if (result) {
            getDistributionList();
            resetForm();
        }
    };

    const handleEdit = (id) => {
        const data = distributionList.find(item => item?.id === id);
        if (data) {
            setEditId(id);
            setSelectedReport(data?.reportType);
            setEmailList(data?.emails);
        }
    };

    const handleDelete = async (id) => {
        const result = await deleteAPI(`/distribution/${id}`);
        if (result) {
            getDistributionList();
            resetForm();
        }
    };

    return (
        <Box p={3}>
            <Grid container spacing={4}>
                {/* Left Side - Form */}
                <Grid item xs={12} md={5}>
                    <Typography variant="h4" fontWeight={600} mb={3}>Distribution</Typography>

                    <FormControl fullWidth sx={{ mb: 3 }}>
                        <InputLabel id="report-type-label">Reports</InputLabel>
                        <Select
                            labelId="report-type-label"
                            value={selectedReport}
                            label="Reports"
                            onChange={(e) => setSelectedReport(e.target.value)}
                        >
                            <MenuItem value="Tenancy check">Tenancy check</MenuItem>
                            <MenuItem value="Shift log">Shift log</MenuItem>
                            <MenuItem value="Panic Alarm">Panic Alarm</MenuItem>
                            <MenuItem value="Risk Assessment">Risk Assessment</MenuItem>
                            {reportTypes.map((type, i) => (
                                <MenuItem key={i} value={type?.name}>{type?.name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <form onSubmit={handleEmailSubmit}>
                        <TextField
                            fullWidth
                            placeholder="Enter email and press Enter"
                            value={emailInput}
                            onChange={handleEmailChange}
                            type="email"
                            sx={{ mb: 2 }}
                        />
                    </form>

                    <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 2 }}>
                        {emailList.map((email, index) => (
                            <Chip
                                key={index}
                                label={email}
                                onDelete={() => handleDeleteEmail(index)}
                                deleteIcon={<CloseIcon />}
                            />
                        ))}
                    </Stack>

                    <Stack direction="row" spacing={2} justifyContent="flex-end">
                        <Button variant="contained" onClick={handleSubmit}>
                            {editId ? 'Edit' : 'Submit'}
                        </Button>
                        <RestartAltIcon onClick={resetForm} sx={{ cursor: 'pointer' }} />
                    </Stack>
                </Grid>

                {/* Right Side - Table */}
                <Grid item xs={12} md={7}>
                    <TableContainer component={Paper}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={tableHeader}>Report Type</TableCell>
                                    <TableCell sx={tableHeader}>Emails</TableCell>
                                    <TableCell sx={tableHeader} align="center">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {distributionList.map((item, idx) => (
                                    <TableRow key={idx}>
                                        <TableCell sx={tableData}>{item?.reportType}</TableCell>
                                        <TableCell sx={tableData}>
                                            {item?.emails?.map((email, i) => (
                                                <div key={i}>{email}</div>
                                            ))}
                                        </TableCell>
                                        <TableCell align="center" sx={tableData}>
                                            <Button
                                                variant="outlined"
                                                color="primary"
                                                onClick={() => handleEdit(item.id)}
                                                sx={{ mr: 1 }}
                                            >
                                                <EditIcon />
                                            </Button>
                                            <Button
                                                variant="outlined"
                                                color="error"
                                                onClick={() => handleDelete(item.id)}
                                            >
                                                <DeleteIcon />
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Grid>
            </Grid>
        </Box>
    );
};

export default Distribution;
